import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { ShoppingBag, Trash, Plus, Minus, ArrowLeft, SealCheck } from '@phosphor-icons/react';
import Navbar from '@/components/custom/Navbar';
import Footer from '@/components/custom/Footer';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CartPage = () => {
  const navigate = useNavigate();
  const { cart, updateQuantity, removeFromCart, loading, sessionId } = useCart();
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    shipping_address: ''
  });

  const handleQuantityChange = async (productId, currentQty, delta) => {
    const newQty = currentQty + delta;
    if (newQty <= 0) {
      await removeFromCart(productId);
      toast.success('Item removed from cart');
    } else {
      await updateQuantity(productId, newQty);
    }
  };

  const handleCheckout = async (e) => {
    e.preventDefault();
    if (!formData.customer_name || !formData.customer_email || !formData.shipping_address) {
      toast.error('Please fill in all fields');
      return;
    }

    setCheckoutLoading(true);
    try {
      const response = await axios.post(`${API}/checkout/create-session`, {
        cart_id: cart.cart_id || sessionId,
        ...formData
      }, {
        headers: {
          'Origin': window.location.origin
        }
      });

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to create checkout session');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const isEmpty = !cart.items || cart.items.length === 0;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-20">
        {/* Back Link */}
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4">
          <Link to="/products" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Continue Shopping
          </Link>
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-12 pb-24">
          <h1 className="text-3xl md:text-4xl font-medium mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
            Your Cart
          </h1>

          {isEmpty ? (
            <div className="text-center py-16" data-testid="empty-cart">
              <ShoppingBag weight="duotone" className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <h2 className="text-xl font-medium mb-2">Your cart is empty</h2>
              <p className="text-muted-foreground mb-6">Discover our sustainable artisan products</p>
              <Link to="/products">
                <Button className="rounded-full">Browse Products</Button>
              </Link>
            </div>
          ) : (
            <div className="grid lg:grid-cols-3 gap-12" data-testid="cart-content">
              {/* Cart Items */}
              <div className="lg:col-span-2 space-y-4">
                {cart.items.map(item => (
                  <Card key={item.product_id} className="p-4 flex gap-4">
                    <Link to={`/products/${item.product_id}`} className="flex-shrink-0">
                      <img
                        src={item.product?.image || 'https://images.pexels.com/photos/32795036/pexels-photo-32795036.jpeg'}
                        alt={item.product?.name}
                        className="w-24 h-24 object-cover rounded-lg"
                      />
                    </Link>
                    <div className="flex-1 min-w-0">
                      <Link to={`/products/${item.product_id}`}>
                        <h3 className="font-medium truncate hover:text-primary transition-colors">
                          {item.product?.name}
                        </h3>
                      </Link>
                      <p className="text-sm text-muted-foreground mb-2">
                        Score: {item.product?.transparency_score?.toFixed(0)}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleQuantityChange(item.product_id, item.quantity, -1)}
                            disabled={loading}
                          >
                            <Minus className="w-4 h-4" />
                          </Button>
                          <span className="w-8 text-center">{item.quantity}</span>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleQuantityChange(item.product_id, item.quantity, 1)}
                            disabled={loading}
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                        <span className="font-semibold">${item.item_total?.toFixed(2)}</span>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-muted-foreground hover:text-destructive"
                      onClick={() => removeFromCart(item.product_id)}
                      disabled={loading}
                      data-testid={`remove-item-${item.product_id}`}
                    >
                      <Trash className="w-5 h-5" />
                    </Button>
                  </Card>
                ))}
              </div>

              {/* Order Summary & Checkout */}
              <div className="lg:col-span-1">
                <Card className="p-6 sticky top-24">
                  <h2 className="text-lg font-medium mb-4">Order Summary</h2>
                  
                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Subtotal</span>
                      <span>${cart.total?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Shipping</span>
                      <span className="text-[#4A6C4C]">Free</span>
                    </div>
                    <Separator />
                    <div className="flex justify-between font-medium">
                      <span>Total</span>
                      <span>${cart.total?.toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Fair Trade Breakdown */}
                  <div className="bg-secondary/50 rounded-lg p-4 mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <SealCheck weight="duotone" className="w-5 h-5 text-[#4A6C4C]" />
                      <span className="text-sm font-medium">Fair Trade Breakdown</span>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Artisan Share (85%)</span>
                        <span className="text-[#4A6C4C] font-medium">${cart.artisan_share?.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Platform (15%)</span>
                        <span>${cart.platform_share?.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Checkout Form */}
                  <form onSubmit={handleCheckout} className="space-y-4">
                    <div>
                      <Label htmlFor="name">Full Name</Label>
                      <Input
                        id="name"
                        value={formData.customer_name}
                        onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                        placeholder="Your name"
                        required
                        data-testid="checkout-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.customer_email}
                        onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                        placeholder="your@email.com"
                        required
                        data-testid="checkout-email"
                      />
                    </div>
                    <div>
                      <Label htmlFor="address">Shipping Address</Label>
                      <Textarea
                        id="address"
                        value={formData.shipping_address}
                        onChange={(e) => setFormData({ ...formData, shipping_address: e.target.value })}
                        placeholder="Enter your shipping address"
                        required
                        rows={3}
                        data-testid="checkout-address"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full h-12 rounded-full"
                      disabled={checkoutLoading}
                      data-testid="checkout-btn"
                    >
                      {checkoutLoading ? 'Processing...' : 'Proceed to Checkout'}
                    </Button>
                  </form>
                </Card>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CartPage;
